import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import styled from "styled-components";

interface ProductDisplayCardProps {
    name: string;
    productImagePath: string;
    supplierName?: string | null;
    startLocationName: string;
    showDeleteButton?: boolean;
    onDelete?: () => void;
    productId: string;
    isExpired?: boolean;
}

export const ItemsByProductHeader: React.FC<ProductDisplayCardProps> = ({
    name,
    productImagePath,
    supplierName,
    startLocationName,
    showDeleteButton = false,
    onDelete,
    productId,
    isExpired = false,
}: ProductDisplayCardProps) => {
    const [searchParams] = useSearchParams();
    const isPending = searchParams.get("isPending") === "true";

    return (
        <HeaderContainer>
            <MainWrapper isExpired={isExpired} isPending={isPending}>
                <Link to={`/detail-tour/${productId}`} className="main">
                    <div className="img">
                        {productImagePath && <img src={productImagePath} alt={name} />}
                    </div>
                    <div className="info-container">
                        <div className="name-container">
                            <p className="title">{name}</p>
                            <p className="sub-title">{supplierName}</p>
                            <span className="location">
                                <img
                                    width={13}
                                    height={16}
                                    src="/images/location_22.svg"
                                    alt="location"
                                />
                                {startLocationName}
                            </span>
                        </div>
                    </div>
                </Link>
            </MainWrapper>
            {showDeleteButton && onDelete && (
                <DeleteButton
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete();
                    }}
                    disabled={isPending}
                >
                    <img src="/images/deleteSvgButton.svg" alt="delete" />
                </DeleteButton>
            )}
        </HeaderContainer>
    );
};

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
`;

const MainWrapper = styled.div<{ isExpired: boolean; isPending: boolean }>`
    opacity: ${({ isExpired }) => (isExpired ? 0.5 : 1)};
    flex: 1;
    .main {
        display: flex;
        gap: 12px;
    }
    .img {
        position: relative;
        cursor: ${({ isPending }) => (isPending ? "default" : "pointer")};
        height: 64px;
        width: 64px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        img {
            height: 100%;
            width: 100%;
            object-fit: cover;
            border-radius: 8px;
        }
    }
    .info-container {
        display: flex;
        justify-content: space-between;
        width: 100%;
        gap: 10px;
    }
    .name-container {
        display: flex;
        flex-direction: column;
        gap: 6px;
        .title {
            width: 100%;
            font-size: 14px;
            font-weight: 600;
            line-height: 23px;
            cursor: ${({ isPending }) => (isPending ? "default" : "pointer")};
            color: #131214;
            text-decoration: underline;
            text-underline-offset: 3px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
            }
            @media (min-width: 1024px) {
                font-size: 18px;
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            }
        }
        .sub-title {
            color: #6e7375;
            font-size: 12px;
            line-height: 14px;
            @media (min-width: 375px) {
                width: 200px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            @media (min-width: 425px) {
                width: 250px;
            }
            @media (min-width: 1024px) {
                width: auto;
                overflow: visible;
                text-overflow: clip;
                white-space: normal;
            }
        }
        .location {
            display: flex;
            align-items: center;
            gap: 6px;
            color: #3c3c3c;
            font-size: 12px;
            font-weight: 500;
        }
    }
`;

const DeleteButton = styled.button<{ disabled: boolean }>`
    background: none;
    border: none;
    padding: 0;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    align-self: flex-start;
    opacity: 1 !important;
`;
